/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { Checkbox } from "@/Components/ui/checkbox"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/Components/ui/form"
import { Input } from "@/Components/ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import { Task } from "./Plan"
import { useContext, useEffect, useState } from 'react'
import { PlanBuilderContext } from './Plan'
import { AppContext } from "@/App"
import DatePicker from "../ui/DatePicker"
import { Check, NotebookTabs, SquareStack } from "lucide-react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog"
import { format } from "date-fns"
import { dateformat, isWorkingDay } from "@/utils/GlobalConstants"
import FrmDependencies from "./frmDependencies"
//import { Textarea } from "../ui/textarea"

const formSchema = z.object({
    id: z.number(),
    name: z.string()
        .min(1, { message: "Name is required" })
        .max(50, { message: "Name must be 50 characters or less" }),
    // description: z.string()
    //     .max(250, { message: "Description must be less than 250 characters" }),
    duration: z.coerce.number({
        required_error: "Duration is required",
        invalid_type_error: "Duration must be a whole number",
    })
        .int()
        .positive()
        .min(1, { message: "Duration should be at least 1" }),
    startDate: z.coerce.date().refine((data) => isWorkingDay(data), {
        message: "Start date must be a working day",
    }),
    finishDate: z.date(),
    ownerId: z.string(),//.nonempty({ message: "Owner is required" }),
    completed: z.boolean(),
    completedDate: z.date().nullable().refine((data) => {
        if (data === null) return true; // Allow null value
        return data <= new Date();
    }, {
        message: "Completed date cannot be in the future",
    }),
    isDeliverable: z.boolean(),
    isMilestone: z.boolean(),
    displayOnCharter: z.boolean(),
    displayOnStatusReport: z.boolean(),
    dependencies: z.number().array(),
}).refine(data => {
    if ((data.completedDate !== null && data.completedDate >= data.startDate) || data.completedDate === null) return true
}, {
    message: "Completed date cannot be before the start date",
    path: ["completedDate"],
});

interface TaskFormProps {
    task: Task
}

const FrmTask = ({ task }: TaskFormProps) => {
    const app = useContext(AppContext);
    const props = useContext(PlanBuilderContext);

    const [selectedDependencies, setSelectedDependencies] = useState<number[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [earliestStartDate, setEarliestStartDate] = useState<Date>(new Date(task.startDate));
    const [allowSubmit, setAllowSubmit] = useState(true);
    const [dependencyOptions, setDependencyOptions] = useState<Task[]>([]);
    const [showCompletedDate, setShowCompletedDate] = useState<boolean>(task.completed);
    const [completeDisabledMessage, setCompleteDisabledMessage] = useState<string>("");

    const { reset, ...form } = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            // create default values for the form
            id: task.id,
            name: task.name,
            //description: task.description,
            duration: task.duration,
            startDate: new Date(task.startDate),
            finishDate: new Date(task.finishDate),
            ownerId: task.ownerId ?? "",
            completed: task.completed,
            completedDate: task.completedDate ? new Date(task.completedDate) : null,
            isDeliverable: task.isDeliverable,
            isMilestone: task.isMilestone,
            displayOnCharter: task.displayOnCharter,
            displayOnStatusReport: task.displayOnStatusReport,
            dependencies: task.dependencies
        },
        mode: "onBlur",
    });

    useEffect(() => {
        reset({
            id: task.id,
            name: task.name,
            //description: task.description,
            duration: task.duration,
            startDate: new Date(task.startDate),
            finishDate: new Date(task.finishDate),
            ownerId: task.ownerId,
            completed: task.completed,
            completedDate: task.completedDate ? new Date(task.completedDate) : null,
            isDeliverable: task.isDeliverable,
            isMilestone: task.isMilestone,
            displayOnCharter: task.displayOnCharter,
            displayOnStatusReport: task.displayOnStatusReport,
            dependencies: task.dependencies
        });
        setDependencyOptions(GetTasksForDependenciesForm());
        setEarliestStartDate(new Date(getEarliestStartDate(task)));
        setShowCompletedDate(task.completed);
        setSelectedDependencies(task.dependencies);

        setTimeout(() => form.setFocus("name"), 0);

    }, [task, reset]);

    useEffect(() => {
        // Debounce the effect to prevent multiple calls when the selected Task changes
        const timeout = setTimeout(() => {
            const startDate = new Date(form.getValues("startDate"));
            earliestStartDate.setHours(0, 0, 0, 0);
            startDate.setHours(0, 0, 0, 0);
            if (props.isWorkingDay(startDate) === false) {
                form.setError("startDate", {
                    type: "manual",
                    message: `Start date must be a working day`,
                });
                setAllowSubmit(false);
                return;
            }
            if (startDate < earliestStartDate) {
                form.setError("startDate", {
                    type: "manual",
                    message: `Start date must be after ${format(earliestStartDate, dateformat)}`,
                });
                setAllowSubmit(false);
            } else {
                form.clearErrors("startDate");
                updateFinishDate();
                setAllowSubmit(true);
            }
        }, 300); // Adjust debounce delay as needed

        return () => clearTimeout(timeout); // Cleanup timeout on dependency change
    }, [earliestStartDate, form.watch("startDate")]);

    useEffect(() => {
        updateDuration();
    }, [form.watch("finishDate")])

    function GetTasksForDependenciesForm() {
        // get a list of tasks that are not the selected task and have the selected task as a dependency
        const dependents: number[] = [];
        props.getDependentTaskIds(task.id, dependents);

        const tasks = props.getAllTasks()?.filter((task) => !dependents.includes(task.id) && task.id !== props.selectedTaskID);
        return tasks ?? [];
    }

    function getEarliestStartDate(task: Task): Date {
        let startDate = new Date(props.projectStartDate);

        if (task.dependencies.length === 0) {
            return startDate;
        }
        else {
            // Update startDate based on the latest finish date of the dependencies
            let latestFinishDate = startDate;
            const options = GetTasksForDependenciesForm();

            task.dependencies.forEach((dependencyID) => {
                const dependency = options.find((task) => task.id === dependencyID) as Task;
                if (dependency && dependency.finishDate > latestFinishDate) {
                    latestFinishDate = dependency.finishDate;
                }
            });

            startDate = latestFinishDate;
        }
        return startDate;
    }

    function handleCompletedChange(checked: boolean): void {
        if (checked) {
            // If the task is completed, set the completed date to today
            form.setValue("completedDate", new Date());
        } else {
            // If the task is not completed, clear the completed date
            form.setValue("completedDate", null);
        }
        setShowCompletedDate(checked);
    }

    function updateSelectedDependencies(selectedDependencies: number[]): void {
        task.dependencies = selectedDependencies;
        const startDate = getEarliestStartDate(task);
        task.startDate = startDate;
        setEarliestStartDate(new Date(startDate));
        form.setValue("startDate", new Date(startDate));
        form.setValue("dependencies", task.dependencies);

        setDialogOpen(false);
    }

    function updateFinishDate(): void {
        // set finish date to start date + duration
        const startDate = new Date(form.getValues("startDate"));
        const duration = form.getValues("duration");

        form.setValue("finishDate", props.addWorkingDays(startDate, duration));
    }

    function updateDuration(): void {
        const startDate = new Date(form.getValues("startDate"));
        const finishDate = new Date(form.getValues("finishDate"));

        form.setValue("duration", props.getWorkingDays(startDate, finishDate));
    }

    function disableComplete(): boolean {
        const startDate = new Date(form.getValues("startDate"));

        // if project life cycle is not execution or closure, disable the checkbox
        if (props.projectLifecycleStage !== "Execution" && props.projectLifecycleStage !== "Closure") {
            setCompleteDisabledMessage("Cannot complete: project is not in execution or closure stage.");
            return true;
        }

        // if start date is in the future, disable the checkbox
        if (startDate > new Date()) {
            setCompleteDisabledMessage("Cannot complete: start date is in the future.");
            return true;
        }
        // if the task has dependencies, check if they are completed
        if (task.dependencies.length > 0) {
            let disable = true;

            selectedDependencies.some((dependency) => {
                const task = dependencyOptions.find((task) => task.id === dependency);
                disable = !task?.completed;
            })

            if (disable) { // only return if true
                setCompleteDisabledMessage("Cannot complete: has uncompleted dependencies.");
                return true;
            }
        }

        if (task.subTasks && task.subTasks.length > 0) {
            // If any subtask is not completed, disable complete
            if (task.subTasks.some((subTask) => !subTask.completed)) {
                setCompleteDisabledMessage("Cannot complete: has uncompleted sub-tasks.");
                return true;
            }
        }
        
        return false;
    }

    function onSubmit(data: z.infer<typeof formSchema>) {

        const updatedTask: Task = {
            ...data,
            id: data.id,
            name: data.name,
            //description: data.description,
            index: task.index,
            workPackageId: task.workPackageId,
            duration: data.duration,
            startDate: new Date(data.startDate),
            finishDate: props.addWorkingDays(data.startDate, data.duration),
            ownerId: data.ownerId == "unassigned" ? "" : data.ownerId,
            completed: data.completed,
            completedDate: data.completed && data.completedDate ? new Date(data.completedDate) : null,
            isDeliverable: data.isDeliverable,
            isMilestone: data.isMilestone,
            displayOnCharter: data.displayOnCharter,
            displayOnStatusReport: data.displayOnStatusReport,
            dependencies: data.dependencies,
            hasDependents: task.hasDependents,
            status: task.status,
        };

        props.updateTask(task.id, updatedTask)
    }

    function onError(errors: any) {
        console.error(errors);
    }

    return (
        <Form {...form} reset={reset}>
            <form onSubmit={form.handleSubmit(onSubmit, onError)} className="flex flex-row w-full">
                <div className="flex flex-col space-y-4 w-1/2">
                    <div className="">
                        <FormField
                            control={form.control}
                            name="name"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Name</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Name" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    {/* <div className="">
                        <FormField
                            control={form.control}
                            name="description"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Description</FormLabel>
                                    <FormControl>
                                        <Textarea placeholder="Description" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /></div> */}
                    <div className="">
                        <FormField
                            control={form.control}
                            name="duration"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Duration</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Duration" {...field} onBlur={updateFinishDate} disabled={showCompletedDate || (task.subTasks && task.subTasks.length > 0)} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /></div>
                    <div className="">
                        <FormField
                            control={form.control}
                            name="startDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Start Date</FormLabel>
                                    <FormControl>
                                        <DatePicker date={new Date(field.value)} setDate={field.onChange} disable={showCompletedDate} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /></div>
                    <div className="">
                        <FormField
                            control={form.control}
                            name="finishDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>End Date</FormLabel>
                                    <FormControl>
                                        <DatePicker date={new Date(field.value)} setDate={field.onChange} disable={showCompletedDate || (task.subTasks && task.subTasks.length > 0)} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /></div>
                    <div>
                        <FormField
                            control={form.control}
                            name="ownerId"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Owner</FormLabel>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Assign to Team Member" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                            {app.teamMembers?.map((t) => (
                                                <SelectItem key={t.id} value={t.id as string}>{t.email}</SelectItem>
                                            ))}
                                            {app.projectManagers?.map((m) => (
                                                <SelectItem key={m.id} value={m.id as string}>{m.email}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
                <div className="flex flex-col pt-4 pl-4 space-y-4 w-1/2">
                    <div className="flex pt-1 w-full relative"> {/* All checkbox container divs must be displayed as relative to not have a scrollbar when the form is hidden. This is due to shadcn control */}
                        <FormField
                            control={form.control}
                            name="completed"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <div className="flex items-center group">
                                            <FormLabel className="mr-2">Completed</FormLabel>
                                            <Checkbox
                                                checked={field.value}
                                                onCheckedChange={(checked) => {
                                                    field.onChange(checked);
                                                    handleCompletedChange(checked as boolean);
                                                }}
                                                disabled={disableComplete()}
                                            />
                                            {disableComplete() && (
                                                <div className="absolute left-8 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-muted text-xs text-foreground px-2 py-1 rounded shadow-lg">
                                                    {completeDisabledMessage}
                                                </div>
                                            )}
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className={showCompletedDate ? "flex pt-1 w-full relative" : "hidden"}>
                        <FormField
                            control={form.control}
                            name="completedDate"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Completed Date</FormLabel>
                                    <FormControl>
                                        <DatePicker
                                            date={field.value ? new Date(field.value) : new Date()}
                                            setDate={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /></div>
                    <div className="w-full relative">
                        <FormField
                            control={form.control}
                            name="isDeliverable"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Deliverable</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="w-full relative">
                        <FormField
                            control={form.control}
                            name="isMilestone"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Milestone</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="w-full relative">
                        <FormField
                            control={form.control}
                            name="displayOnCharter"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Display on Charter</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="w-full relative">
                        <FormField
                            control={form.control}
                            name="displayOnStatusReport"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="mr-2">Display on Status Report</FormLabel>
                                    <FormControl>
                                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="flex flex-row w-full">
                        <FormField
                            control={form.control}
                            name="dependencies"
                            render={({ field }) => (
                                <FormItem className="flex flex-col w-full">
                                    <FormLabel>Dependencies</FormLabel>
                                    <div className="flex flex-row grow ">
                                        <div className="flex flex-col border border-input rounded-md grow">
                                            {field.value.map((dependency) => {
                                                const currDep = props.getTaskById(dependency);
                                                return <div className={`flex items-center justify-between text-sm rounded-md p-2 ${currDep?.isDeliverable ? "bg-planbuilder-deliverable" : ""} hover: cursor-pointer`} key={dependency} onClick={() => props.selectTask(dependency)}>
                                                    <div className="max-w-[140px] truncate">{currDep?.name}</div>{currDep?.completed ? <Check className="bg-green-600 rounded-md h-4 w-4" /> : <div className=""></div>}
                                                </div>;
                                            })}
                                        </div>
                                        {/* if completed checkbox is ticked, hide the trigger */}

                                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                                            <DialogTrigger disabled={form.watch("completed")}><div className="align-bottom ml-2" onClick={(e) => e.preventDefault}><SquareStack className={form.watch("completed") ? "text-muted-foreground" : ""} /></div></DialogTrigger>
                                            <DialogContent>
                                                <DialogHeader>
                                                    <DialogTitle>Add Dependencies</DialogTitle>
                                                    <DialogDescription>
                                                    </DialogDescription>
                                                </DialogHeader>
                                                <FrmDependencies
                                                    task={task}
                                                    preselectedDependencies={selectedDependencies}
                                                    onSubmit={(updateSelectedDependencies)}
                                                />
                                            </DialogContent>
                                        </Dialog>
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className="">
                        <button type="submit" className="flex flex-row items-center button-default w-full mr-2" disabled={!allowSubmit}><NotebookTabs className="w-6 h-6 mr-2" />Update Task</button>
                    </div>
                </div>
            </form>
        </Form>
    )
}

export default FrmTask